import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Search, Loader2, RefreshCw, ChevronLeft, ChevronRight,
  Pencil, Save, Plus, Upload, CheckCircle2, XCircle, FileSpreadsheet,
  Download, Trash2, FileText, Users, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import adminService from '../../services/adminService';

// ── Option lists ───────────────────────────────────────────────────────────────

const LICENSE_STATUSES   = ['Active', 'Inactive', 'Pending', 'Suspended', 'Expired', 'Revoked', 'Cancelled'];
const LICENSE_TYPES      = ['Cultivation', 'Processing', 'Retail', 'Distribution', 'Laboratory Testing', 'Manufacturing', 'Dispensary', 'Delivery', 'Wholesale', 'Microbusiness', 'Other'];
const LICENSE_DESIGNATIONS = ['Tier 1', 'Tier 2', 'Tier 3', 'Standard', 'Medical Only', 'Adult Use Only', 'Dual Use', 'Provisional', 'Annual', 'Conditional', 'Other'];
const LICENSE_VERTICALS  = ['Cannabis', 'Hemp', 'Agriculture', 'CBD', 'Other'];
const LICENSE_ACTIVITIES = ['Cultivation', 'Retail Sales', 'Manufacturing', 'Distribution', 'Testing', 'Research', 'Delivery', 'Wholesale', 'Other'];
const LICENSE_CATEGORIES = ['Medical', 'Adult Use', 'Recreational', 'Dual Use', 'Research', 'Other'];

// Builds SelectItems from a static list, ensuring current value appears even if not in list
function optionItems(list, currentValue) {
  const all = currentValue && !list.includes(currentValue) ? [...list, currentValue] : list;
  return all.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>);
}

function ComboSelect({ value, onValueChange, options, placeholder }) {
  const normalized = value || 'none';
  return (
    <Select value={normalized} onValueChange={v => onValueChange(v === 'none' ? '' : v)}>
      <SelectTrigger><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">— None —</SelectItem>
        {optionItems(options, value)}
      </SelectContent>
    </Select>
  );
}

// ── Validation helpers ─────────────────────────────────────────────────────────

const PHONE_RE = /^\+?[\d\s\-().]{7,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateOperatorForm(form) {
  const errors = [];
  if (!form.businessName?.trim()) errors.push('Business Name is required.');
  if (form.phone?.trim() && !PHONE_RE.test(form.phone.trim()))
    errors.push('Phone number format is invalid.');
  if (form.email?.trim() && !EMAIL_RE.test(form.email.trim()))
    errors.push('Email address format is invalid.');
  form.contacts?.forEach((c, i) => {
    if (c.contactPhone?.trim() && !PHONE_RE.test(c.contactPhone.trim()))
      errors.push(`Contact ${i + 1}: Phone number format is invalid.`);
    if (c.contactEmail?.trim() && !EMAIL_RE.test(c.contactEmail.trim()))
      errors.push(`Contact ${i + 1}: Email address format is invalid.`);
  });
  return errors;
}

// ── EMPTY templates ────────────────────────────────────────────────────────────

const EMPTY_LICENSE = {
  licenseNumber: '', licenseStateId: null, licenseType: '', licenseDesignation: '',
  licenseStatus: '', licenseCategory: '', licenseVertical: '', licenseActivity: '',
  licenseTerm: '', licenseIssuedDate: '', licenseExpirationDate: '',
};

const EMPTY_CONTACT = {
  contactType: '', contactFirstName: '', contactLastName: '',
  contactAddress: '', contactPhone: '', contactEmail: '',
};

function newOperator() {
  return {
    businessName: '', corporateName: '', dbaName: '', phone: '', email: '', website: '',
    streetAddress: '', city: '', county: '', zipCode: '',
    latitude: '', longitude: '',
    facebook: '', instagram: '', twitter: '', linkedin: '',
    energySupplier: '', certificateBody: '', notes: '',
    licenses: [{ ...EMPTY_LICENSE }],
    contacts: [{ ...EMPTY_CONTACT }],
  };
}

// ── LicenseItem ────────────────────────────────────────────────────────────────

function LicenseItem({ license, index, onChange, onRemove, canRemove, states }) {
  const set = (field, value) => onChange(index, { ...license, [field]: value });
  return (
    <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-muted-foreground uppercase">License {index + 1}</p>
        {canRemove && (
          <Button variant="ghost" size="sm" onClick={() => onRemove(index)}
            className="text-destructive hover:text-destructive h-6 w-6 p-0">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">License Number</Label>
          <Input placeholder="LIC-000000" value={license.licenseNumber} onChange={e => set('licenseNumber', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">State</Label>
          <Select
            value={license.licenseStateId != null ? String(license.licenseStateId) : 'none'}
            onValueChange={v => set('licenseStateId', v === 'none' ? null : Number(v))}>
            <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— None —</SelectItem>
              {states.map(s => <SelectItem key={s.stateId} value={String(s.stateId)}>{s.abbreviation} — {s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <ComboSelect value={license.licenseStatus} onValueChange={v => set('licenseStatus', v)}
            options={LICENSE_STATUSES} placeholder="Select status" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">License Type</Label>
          <ComboSelect value={license.licenseType} onValueChange={v => set('licenseType', v)}
            options={LICENSE_TYPES} placeholder="Select type" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Designation</Label>
          <ComboSelect value={license.licenseDesignation} onValueChange={v => set('licenseDesignation', v)}
            options={LICENSE_DESIGNATIONS} placeholder="Select designation" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Category</Label>
          <ComboSelect value={license.licenseCategory} onValueChange={v => set('licenseCategory', v)}
            options={LICENSE_CATEGORIES} placeholder="Select category" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Vertical</Label>
          <ComboSelect value={license.licenseVertical} onValueChange={v => set('licenseVertical', v)}
            options={LICENSE_VERTICALS} placeholder="Select vertical" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Activity</Label>
          <ComboSelect value={license.licenseActivity} onValueChange={v => set('licenseActivity', v)}
            options={LICENSE_ACTIVITIES} placeholder="Select activity" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Term</Label>
          <Input placeholder="e.g. 1 Year" value={license.licenseTerm} onChange={e => set('licenseTerm', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Issued Date</Label>
          <Input type="date" value={license.licenseIssuedDate} onChange={e => set('licenseIssuedDate', e.target.value)} />
        </div>
        <div className="space-y-1 col-span-2">
          <Label className="text-xs">Expiration Date</Label>
          <Input type="date" value={license.licenseExpirationDate} onChange={e => set('licenseExpirationDate', e.target.value)} />
        </div>
      </div>
    </div>
  );
}

// ── ContactItem ────────────────────────────────────────────────────────────────

function ContactItem({ contact, index, onChange, onRemove, canRemove }) {
  const set = (field, value) => onChange(index, { ...contact, [field]: value });
  const phoneErr = contact.contactPhone?.trim() && !PHONE_RE.test(contact.contactPhone.trim());
  const emailErr = contact.contactEmail?.trim() && !EMAIL_RE.test(contact.contactEmail.trim());
  return (
    <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-muted-foreground uppercase">Contact {index + 1}</p>
        {canRemove && (
          <Button variant="ghost" size="sm" onClick={() => onRemove(index)}
            className="text-destructive hover:text-destructive h-6 w-6 p-0">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Contact Type</Label>
          <Input placeholder="e.g. Owner" value={contact.contactType} onChange={e => set('contactType', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">First Name</Label>
          <Input placeholder="First name" value={contact.contactFirstName} onChange={e => set('contactFirstName', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Last Name</Label>
          <Input placeholder="Last name" value={contact.contactLastName} onChange={e => set('contactLastName', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Phone</Label>
          <Input placeholder="(555) 000-0000" value={contact.contactPhone} onChange={e => set('contactPhone', e.target.value)}
            className={phoneErr ? 'border-destructive' : ''} />
          {phoneErr && <p className="text-xs text-destructive">Invalid phone format</p>}
        </div>
        <div className="space-y-1 col-span-2">
          <Label className="text-xs">Email</Label>
          <Input type="email" placeholder="email@business.com" value={contact.contactEmail} onChange={e => set('contactEmail', e.target.value)}
            className={emailErr ? 'border-destructive' : ''} />
          {emailErr && <p className="text-xs text-destructive">Invalid email format</p>}
        </div>
        <div className="space-y-1 col-span-2">
          <Label className="text-xs">Address</Label>
          <Input placeholder="Address" value={contact.contactAddress} onChange={e => set('contactAddress', e.target.value)} />
        </div>
      </div>
    </div>
  );
}

// ── OperatorFields ─────────────────────────────────────────────────────────────

function OperatorFields({ form, onChange }) {
  const set = (field, value) => onChange(p => ({ ...p, [field]: value }));
  const field = (key, label, span, type) => (
    <div key={key} className={`space-y-1 ${span || ''}`}>
      <Label>{label}</Label>
      <Input type={type || 'text'} value={form[key] ?? ''} onChange={e => set(key, e.target.value)} />
    </div>
  );
  const phoneErr = form.phone?.trim() && !PHONE_RE.test(form.phone.trim());
  const emailErr = form.email?.trim() && !EMAIL_RE.test(form.email.trim());
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1 col-span-2">
        <Label>Business Name *</Label>
        <Input placeholder="Legal business name" value={form.businessName} onChange={e => set('businessName', e.target.value)} />
      </div>
      {field('corporateName', 'Corporate Name (unique identifier)', 'col-span-2')}
      {field('dbaName', 'DBA Name')}
      <div className="space-y-1">
        <Label>Phone</Label>
        <Input placeholder="(555) 000-0000" value={form.phone ?? ''} onChange={e => set('phone', e.target.value)}
          className={phoneErr ? 'border-destructive' : ''} />
        {phoneErr && <p className="text-xs text-destructive">Invalid phone format</p>}
      </div>
      <div className="space-y-1">
        <Label>Email</Label>
        <Input type="email" placeholder="email@business.com" value={form.email ?? ''} onChange={e => set('email', e.target.value)}
          className={emailErr ? 'border-destructive' : ''} />
        {emailErr && <p className="text-xs text-destructive">Invalid email format</p>}
      </div>
      {field('website', 'Website')}
      {field('streetAddress', 'Street Address', 'col-span-2')}
      {field('city', 'City')}
      {field('county', 'County')}
      {field('zipCode', 'Zip Code')}
      {field('latitude', 'Latitude')}
      {field('longitude', 'Longitude')}
      {field('energySupplier', 'Energy Supplier')}
      {field('certificateBody', 'Certificate Body')}
      {field('facebook', 'Facebook')}
      {field('instagram', 'Instagram')}
      {field('twitter', 'Twitter / X')}
      {field('linkedin', 'LinkedIn')}
      {field('notes', 'Notes', 'col-span-2')}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminOperatorsPage() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pageSize: 25, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [states, setStates] = useState([]);

  // Edit dialog
  const [editOp, setEditOp] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Add dialog
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(newOperator);
  const [isAdding, setIsAdding] = useState(false);

  // Delete confirm dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Bulk upload
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // ── Template download ───────────────────────────────────────────────────────

  const downloadTemplate = () => {
    const headers = [
      'BusinessName','CorporateName','DbaName','Phone','Email','Website',
      'StreetAddress','City','County','ZipCode','Latitude','Longitude',
      'Facebook','Instagram','Twitter','Linkedin','EnergySupplier','CertificateBody','Notes',
      'License1_Number','License1_State','License1_Type','License1_Designation',
      'License1_Status','License1_Category','License1_Vertical','License1_Activity',
      'License1_Term','License1_IssuedDate','License1_ExpirationDate',
      'License2_Number','License2_State','License2_Type','License2_Designation',
      'License2_Status','License2_Category','License2_Vertical','License2_Activity',
      'License2_Term','License2_IssuedDate','License2_ExpirationDate',
      'Contact1_Type','Contact1_FirstName','Contact1_LastName',
      'Contact1_Address','Contact1_Phone','Contact1_Email',
      'Contact2_Type','Contact2_FirstName','Contact2_LastName',
      'Contact2_Address','Contact2_Phone','Contact2_Email',
    ];
    const example = [
      'Green Leaf Cannabis Co.','Green Leaf Cannabis Co.','Green Leaf','(555) 123-4567','contact@greenleaf.com','https://greenleaf.com',
      '123 Main St','Denver','Denver County','80203','39.7392','-104.9903',
      '','','','','Xcel Energy','','Example operator',
      'LIC-20240001','CO','Cultivation','Tier 1','Active','Medical','Cannabis','Cultivation','1 Year','2024-01-15','2025-01-14',
      'LIC-20240002','WA','Retail','Standard','Active','Adult Use','Cannabis','Retail Sales','1 Year','2024-03-01','2025-02-28',
      'Owner','Jane','Smith','123 Main St Denver CO','(555) 987-6543','jane@greenleaf.com',
      'Manager','Bob','Jones','456 Oak Ave Denver CO','(555) 111-2222','bob@greenleaf.com',
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 4, 18) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Operators');
    XLSX.writeFile(wb, 'operators_template.xlsx');
    toast.success('Template downloaded');
  };

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetchOperators = useCallback(async () => {
    setIsLoading(true);
    try {
      const filter = { page, pageSize: 25 };
      if (search) filter.search = search;
      if (stateFilter) filter.state = stateFilter;
      const result = await adminService.getOperators(filter);
      setData(result);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to load operators');
    } finally {
      setIsLoading(false);
    }
  }, [search, stateFilter, page]);

  useEffect(() => { fetchOperators(); }, [fetchOperators]);

  useEffect(() => {
    adminService.getStates().then(setStates).catch(() => {});
  }, []);

  // ── Edit ────────────────────────────────────────────────────────────────────

  const openEdit = async (op) => {
    setIsEditOpen(true);
    setIsLoadingEdit(true);
    try {
      const full = await adminService.getOperator(op.operatorId);
      const toLic = l => ({
        licenseNumber:        l.licenseNumber ?? '',
        licenseStateId:       l.stateId ?? null,
        licenseType:          l.licenseType ?? '',
        licenseDesignation:   l.licenseDesignation ?? '',
        licenseStatus:        l.licenseStatus ?? '',
        licenseCategory:      l.category ?? '',
        licenseVertical:      l.vertical ?? '',
        licenseActivity:      l.activity ?? '',
        licenseTerm:          l.licenseTerm ?? '',
        licenseIssuedDate:    l.issueDate ? l.issueDate.split('T')[0] : '',
        licenseExpirationDate: l.expirationDate ? l.expirationDate.split('T')[0] : '',
      });
      const toCon = c => ({
        contactType:      c.contactType ?? '',
        contactFirstName: c.firstName ?? '',
        contactLastName:  c.lastName ?? '',
        contactAddress:   c.address ?? '',
        contactPhone:     c.phone ?? '',
        contactEmail:     c.email ?? '',
      });
      setEditOp({
        operatorId:      full.operatorId,
        businessName:    full.businessName ?? '',
        corporateName:   full.corporateName ?? '',
        dbaName:         full.dbaName ?? '',
        phone:           full.phone ?? '',
        email:           full.email ?? '',
        website:         full.website ?? '',
        streetAddress:   full.streetAddress ?? '',
        city:            full.city ?? '',
        county:          full.county ?? '',
        zipCode:         full.zipCode ?? '',
        latitude:        full.latitude ?? '',
        longitude:       full.longitude ?? '',
        facebook:        full.facebook ?? '',
        instagram:       full.instagram ?? '',
        twitter:         full.twitter ?? '',
        linkedin:        full.linkedin ?? '',
        energySupplier:   full.energySupplier ?? '',
        certificateBody:  full.certificateBody ?? '',
        notes:            full.notes ?? '',
        stateCount:       full.stateCount ?? null,
        licenseCount:     full.licenseCount ?? null,
        licensetypeCount: full.licensetypeCount ?? null,
        licenses: full.licenses?.length > 0 ? full.licenses.map(toLic) : [{ ...EMPTY_LICENSE }],
        contacts: full.contacts?.length > 0 ? full.contacts.map(toCon) : [{ ...EMPTY_CONTACT }],
      });
    } catch {
      toast.error('Failed to load operator details');
      setIsEditOpen(false);
    } finally {
      setIsLoadingEdit(false);
    }
  };

  const handleSave = async () => {
    if (!editOp) return;
    const errs = validateOperatorForm(editOp);
    if (errs.length > 0) { toast.error(errs[0]); return; }
    setIsSaving(true);
    try {
      const { operatorId, licenses, contacts, ...rest } = editOp;
      await adminService.updateOperator(operatorId, { ...rest, licenses, contacts });
      toast.success('Operator updated');
      setIsEditOpen(false);
      fetchOperators();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to update operator');
    } finally {
      setIsSaving(false);
    }
  };

  const upEditLic = (i, v) => setEditOp(p => ({ ...p, licenses: p.licenses.map((l, j) => j === i ? v : l) }));
  const rmEditLic = (i)    => setEditOp(p => ({ ...p, licenses: p.licenses.filter((_, j) => j !== i) }));
  const addEditLic = ()    => setEditOp(p => ({ ...p, licenses: [...p.licenses, { ...EMPTY_LICENSE }] }));
  const upEditCon = (i, v) => setEditOp(p => ({ ...p, contacts: p.contacts.map((c, j) => j === i ? v : c) }));
  const rmEditCon = (i)    => setEditOp(p => ({ ...p, contacts: p.contacts.filter((_, j) => j !== i) }));
  const addEditCon = ()    => setEditOp(p => ({ ...p, contacts: [...p.contacts, { ...EMPTY_CONTACT }] }));

  // ── Add ─────────────────────────────────────────────────────────────────────

  const handleAdd = async () => {
    const errs = validateOperatorForm(addForm);
    if (errs.length > 0) { toast.error(errs[0]); return; }
    setIsAdding(true);
    try {
      await adminService.createOperator(addForm);
      toast.success('Operator created');
      setIsAddOpen(false);
      setAddForm(newOperator());
      fetchOperators();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to create operator');
    } finally {
      setIsAdding(false);
    }
  };

  const upAddLic = (i, v) => setAddForm(p => ({ ...p, licenses: p.licenses.map((l, j) => j === i ? v : l) }));
  const rmAddLic = (i)    => setAddForm(p => ({ ...p, licenses: p.licenses.filter((_, j) => j !== i) }));
  const addAddLic = ()    => setAddForm(p => ({ ...p, licenses: [...p.licenses, { ...EMPTY_LICENSE }] }));
  const upAddCon = (i, v) => setAddForm(p => ({ ...p, contacts: p.contacts.map((c, j) => j === i ? v : c) }));
  const rmAddCon = (i)    => setAddForm(p => ({ ...p, contacts: p.contacts.filter((_, j) => j !== i) }));
  const addAddCon = ()    => setAddForm(p => ({ ...p, contacts: [...p.contacts, { ...EMPTY_CONTACT }] }));

  // ── Delete ──────────────────────────────────────────────────────────────────

  const confirmDelete = (op) => setDeleteTarget(op);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await adminService.deleteOperator(deleteTarget.operatorId);
      toast.success(`"${deleteTarget.businessName}" deleted`);
      setDeleteTarget(null);
      fetchOperators();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to delete operator');
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Bulk upload ─────────────────────────────────────────────────────────────

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setIsUploading(true);
    setBulkResult(null);
    try {
      const result = await adminService.bulkUploadOperators(file);
      setBulkResult(result);
      toast.success(`Bulk upload complete: ${result.created} created, ${result.updated ?? 0} updated`);
      fetchOperators();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Bulk upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Operators (Admin)</h1>
            <p className="text-muted-foreground mt-1">Manage all operators — add individually or bulk upload from Excel</p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Button variant="outline" size="sm" onClick={fetchOperators}>
              <RefreshCw className="h-4 w-4 mr-2" />Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="h-4 w-4 mr-2" />Download Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
              {isUploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Bulk Upload Excel
            </Button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUpload} />
            <Button size="sm" onClick={() => { setAddForm(newOperator()); setIsAddOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />Add Operator
            </Button>
          </div>
        </div>

        {/* Format hint */}
        <Card className="border-dashed">
          <CardContent className="p-4 flex items-start gap-3">
            <FileSpreadsheet className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground space-y-1">
              <p><span className="font-medium text-foreground">Operator: </span>BusinessName*, CorporateName (for upsert), DbaName, Phone, Email, Website, StreetAddress, City, County, ZipCode, ...</p>
              <p><span className="font-medium text-foreground">Licenses: </span>License1_Number, License1_State, License1_Type, License1_Status, ... License2_Number, ... (numbered columns)</p>
              <p><span className="font-medium text-foreground">Contacts: </span>Contact1_Type, Contact1_FirstName, Contact1_LastName, Contact1_Email, ... (numbered columns)</p>
            </div>
          </CardContent>
        </Card>

        {/* Bulk result */}
        {bulkResult && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="h-5 w-5" /><span className="font-semibold">{bulkResult.created} created</span>
                </div>
                {bulkResult.updated > 0 && (
                  <div className="flex items-center gap-2 text-blue-700">
                    <RefreshCw className="h-5 w-5" /><span className="font-semibold">{bulkResult.updated} updated</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-red-600">
                  <XCircle className="h-5 w-5" /><span className="font-semibold">{bulkResult.failed} failed</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setBulkResult(null)}>Dismiss</Button>
              </div>
              {bulkResult.errors?.length > 0 && (
                <div className="mt-3 space-y-1">
                  {bulkResult.errors.map((err, i) => <p key={i} className="text-xs text-red-600">• {err}</p>)}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <form onSubmit={e => { e.preventDefault(); setPage(1); fetchOperators(); }} className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search by business name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
              </div>
              <Select value={stateFilter || 'all'} onValueChange={v => { setStateFilter(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="w-44"><SelectValue placeholder="All States" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {states.map(s => <SelectItem key={s.stateId} value={s.abbreviation}>{s.abbreviation} — {s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="submit">Search</Button>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle>Operators</CardTitle>
            <CardDescription>{data.total} total operators</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Business Name</TableHead>
                        <TableHead>Corporate Name</TableHead>
                        <TableHead>DBA</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>States</TableHead>
                        <TableHead>Licenses</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items?.map(op => (
                        <TableRow key={op.operatorId}>
                          <TableCell className="font-medium">{op.businessName}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">{op.corporateName || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{op.dbaName || '—'}</TableCell>
                          <TableCell>{op.city || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{op.phone || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{op.email || '—'}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {op.stateCount ?? [...new Set((op.licenses || []).map(l => l.state).filter(Boolean))].length} state{(op.stateCount ?? 1) !== 1 ? 's' : ''}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{op.licenses?.length ?? 0} license{op.licenses?.length !== 1 ? 's' : ''}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEdit(op)} title="Edit">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => confirmDelete(op)}
                                className="text-destructive hover:text-destructive" title="Delete">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {data.page} of {data.totalPages} ({data.total} records)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Edit Dialog ──────────────────────────────────────────────────────── */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Operator</DialogTitle>
            <DialogDescription>Update operator details, licenses, and contacts</DialogDescription>
          </DialogHeader>
          {isLoadingEdit ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : editOp && (
            <div className="space-y-6">
              {(editOp.stateCount != null || editOp.licenseCount != null) && (
                <div className="flex gap-4 text-sm text-muted-foreground bg-muted/30 px-4 py-2 rounded-md">
                  {editOp.stateCount != null && <span><strong>{editOp.stateCount}</strong> state{editOp.stateCount !== 1 ? 's' : ''}</span>}
                  {editOp.licenseCount != null && <span><strong>{editOp.licenseCount}</strong> license{editOp.licenseCount !== 1 ? 's' : ''}</span>}
                  {editOp.licensetypeCount != null && <span><strong>{editOp.licensetypeCount}</strong> license type{editOp.licensetypeCount !== 1 ? 's' : ''}</span>}
                </div>
              )}
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-3">Operator Information</p>
                <OperatorFields form={editOp} onChange={setEditOp} />
              </div>

              {/* Licenses */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                    <FileText className="h-3.5 w-3.5" />Licenses ({editOp.licenses.length})
                  </p>
                  <Button variant="outline" size="sm" onClick={addEditLic}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Add License
                  </Button>
                </div>
                <div className="space-y-3">
                  {editOp.licenses.map((lic, i) => (
                    <LicenseItem key={i} index={i} license={lic}
                      onChange={upEditLic} onRemove={rmEditLic}
                      canRemove={editOp.licenses.length > 1} states={states} />
                  ))}
                </div>
              </div>

              {/* Contacts */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />Contacts ({editOp.contacts.length})
                  </p>
                  <Button variant="outline" size="sm" onClick={addEditCon}>
                    <Plus className="h-3.5 w-3.5 mr-1" />Add Contact
                  </Button>
                </div>
                <div className="space-y-3">
                  {editOp.contacts.map((con, i) => (
                    <ContactItem key={i} index={i} contact={con}
                      onChange={upEditCon} onRemove={rmEditCon}
                      canRemove={editOp.contacts.length > 1} />
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button className="flex-1" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setIsEditOpen(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Add Operator Dialog ───────────────────────────────────────────────── */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Operator</DialogTitle>
            <DialogDescription>Business Name is required. Add as many licenses and contacts as needed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground mb-3">Operator Information</p>
              <OperatorFields form={addForm} onChange={setAddForm} />
            </div>

            {/* Licenses */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5" />Licenses ({addForm.licenses.length})
                </p>
                <Button variant="outline" size="sm" onClick={addAddLic}>
                  <Plus className="h-3.5 w-3.5 mr-1" />Add License
                </Button>
              </div>
              <div className="space-y-3">
                {addForm.licenses.map((lic, i) => (
                  <LicenseItem key={i} index={i} license={lic}
                    onChange={upAddLic} onRemove={rmAddLic}
                    canRemove={addForm.licenses.length > 1} states={states} />
                ))}
              </div>
            </div>

            {/* Contacts */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />Contacts ({addForm.contacts.length})
                </p>
                <Button variant="outline" size="sm" onClick={addAddCon}>
                  <Plus className="h-3.5 w-3.5 mr-1" />Add Contact
                </Button>
              </div>
              <div className="space-y-3">
                {addForm.contacts.map((con, i) => (
                  <ContactItem key={i} index={i} contact={con}
                    onChange={upAddCon} onRemove={rmAddCon}
                    canRemove={addForm.contacts.length > 1} />
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button className="flex-1" onClick={handleAdd} disabled={isAdding}>
                {isAdding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Create Operator
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setIsAddOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm Dialog ─────────────────────────────────────────────── */}
      <Dialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />Delete Operator
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <span className="font-semibold text-foreground">{deleteTarget?.businessName}</span> and all
              associated licenses and contacts. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
